import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Popover} from 'antd';
import {StyledSuggestionTag, StyledSuggestionPopoverSpace} from './StyledSuggestionPopover';
import {expensesHelpers, textHelpers} from '../../../../utils/helpers';

const normalizeText = (value = '') => value.trim().toLowerCase();

const SuggestionPopover = ({
  children,
  onSelect,
  value,
  onChange,
  onBlur,
  onFocus,
}) => {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState(expensesHelpers.getExpenseSuggestions());
  const inputValue = typeof value === 'string' ? value : '';

  const filteredSuggestions = useMemo(() => {
    if (!suggestions.length) return [];
    const normalizedValue = normalizeText(inputValue);
    if (!normalizedValue) return suggestions;
    return suggestions.filter((suggestion) =>
      normalizeText(suggestion).includes(normalizedValue) && suggestion.toLowerCase() !== normalizedValue.toLowerCase()
    );
  }, [suggestions, inputValue]);

  useEffect(() => {
    if (!open) return;
    if (filteredSuggestions.length === 0) {
      setOpen(false);
    }
  }, [filteredSuggestions, open]);

  const handleSelect = (suggestion) => {
    onSelect?.(suggestion);
    setOpen(false);
  };

  const handleRemove = (suggestion) => {
    const newSuggestions = suggestions.filter((s) => s !== suggestion);
    setSuggestions(newSuggestions);
    expensesHelpers.removeExpenseSuggestion(suggestion);
  }

  const handleOpen = () => {
    if (filteredSuggestions.length > 0) {
      setOpen(true);
    }
  };

  if (!children) return null;

  return (
    <Popover
      placement='bottomLeft'
      trigger='click'
      open={open}
      onOpenChange={(nextOpen) => setOpen(nextOpen && suggestions.length > 0)}
      content={
        filteredSuggestions.length > 0 ? (
          <StyledSuggestionPopoverSpace direction='horizontal' size='small' wrap>
            {filteredSuggestions.map((suggestion) => (
              <StyledSuggestionTag
                closable={true}
                key={suggestion}
                type='default'
                onClick={() => handleSelect(suggestion)}
                onClose={() => handleRemove(suggestion)}
              >
                {textHelpers.cutString(suggestion, 40)}
              </StyledSuggestionTag>
            ))}
          </StyledSuggestionPopoverSpace>
        ) : null
      }
    >
      {React.cloneElement(children, {
        value,
        onChange: (event) => {
          children.props?.onChange?.(event);
          onChange?.(event);
        },
        onBlur: (event) => {
          children.props?.onBlur?.(event);
          onBlur?.(event);
        },
        onFocus: (event) => {
          children.props?.onFocus?.(event);
          onFocus?.(event);
          handleOpen();
        },
        onClick: (event) => {
          children.props?.onClick?.(event);
          handleOpen();
        }
      })}
    </Popover>
  );
};

SuggestionPopover.propTypes = {
  children: PropTypes.node,
  onSelect: PropTypes.func,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default SuggestionPopover;

